import { create } from 'zustand';

export interface ChatToolCall {
  tool: string;
  success: boolean;
}

export interface ChatActivity {
  id: string;
  message: string;
  status: 'active' | 'done' | 'error';
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  toolCalls?: ChatToolCall[];
  activities?: ChatActivity[];
  isStreaming?: boolean;
}

interface ChatState {
  isOpen: boolean;
  messages: ChatMessage[];
  isStreaming: boolean;
  streamText: string;
  streamToolCalls: ChatToolCall[];
  streamActivities: ChatActivity[];
  streamingMessageId: string | null;
  toolStatus: string | null;
  error: string | null;

  toggle: () => void;
  setOpen: (open: boolean) => void;
  addUserMessage: (text: string) => void;
  startStream: () => void;
  appendStreamText: (text: string) => void;
  setToolStatus: (tool: string | null) => void;
  addToolCall: (tool: string, success: boolean) => void;
  addActivity: (message: string, status?: 'active' | 'done' | 'error') => void;
  updateLastActivity: (message: string, status: 'done' | 'error') => void;
  completeLastActivity: (status?: 'done' | 'error') => void;
  finalizeMessage: (finalText?: string) => void;
  setError: (error: string | null) => void;
  clearMessages: () => void;
}

let messageCounter = 0;
let activityCounter = 0;

export const useChatStore = create<ChatState>((set) => ({
  isOpen: false,
  messages: [],
  isStreaming: false,
  streamText: '',
  streamToolCalls: [],
  streamActivities: [],
  streamingMessageId: null,
  toolStatus: null,
  error: null,

  toggle: () => set((s) => ({ isOpen: !s.isOpen })),
  setOpen: (open) => set({ isOpen: open }),

  addUserMessage: (text) =>
    set((s) => ({
      messages: [
        ...s.messages,
        {
          id: `msg-${++messageCounter}`,
          role: 'user',
          content: text,
          timestamp: new Date(),
        },
      ],
      error: null,
    })),

  startStream: () => {
    const msgId = `msg-${++messageCounter}`;
    return set((s) => ({
      isStreaming: true,
      streamText: '',
      streamToolCalls: [],
      streamActivities: [],
      streamingMessageId: msgId,
      toolStatus: null,
      error: null,
      messages: [
        ...s.messages,
        {
          id: msgId,
          role: 'assistant',
          content: '',
          timestamp: new Date(),
          isStreaming: true,
          activities: [],
          toolCalls: [],
        },
      ],
    }));
  },

  appendStreamText: (text) =>
    set((s) => {
      const newStreamText = s.streamText + text;
      const messages = [...s.messages];
      const lastIdx = messages.length - 1;
      if (lastIdx >= 0 && messages[lastIdx].id === s.streamingMessageId) {
        messages[lastIdx] = { ...messages[lastIdx], content: newStreamText };
      }
      return { streamText: newStreamText, messages };
    }),

  setToolStatus: (tool) => set({ toolStatus: tool }),

  addToolCall: (tool, success) =>
    set((s) => {
      const newToolCalls = [...s.streamToolCalls, { tool, success }];
      const messages = [...s.messages];
      const lastIdx = messages.length - 1;
      if (lastIdx >= 0 && messages[lastIdx].id === s.streamingMessageId) {
        messages[lastIdx] = { ...messages[lastIdx], toolCalls: newToolCalls };
      }
      return { streamToolCalls: newToolCalls, toolStatus: null, messages };
    }),

  addActivity: (message, status = 'active') =>
    set((s) => {
      // Mark previous active activity as done
      const updated = s.streamActivities.map((a) =>
        a.status === 'active' ? { ...a, status: 'done' as const } : a
      );
      const newActivities = [
        ...updated,
        { id: `act-${++activityCounter}`, message, status },
      ];
      const messages = [...s.messages];
      const lastIdx = messages.length - 1;
      if (lastIdx >= 0 && messages[lastIdx].id === s.streamingMessageId) {
        messages[lastIdx] = { ...messages[lastIdx], activities: newActivities };
      }
      return { streamActivities: newActivities, messages };
    }),

  updateLastActivity: (message, status) =>
    set((s) => {
      const activities = [...s.streamActivities];
      for (let i = activities.length - 1; i >= 0; i--) {
        if (activities[i].status === 'active') {
          activities[i] = { ...activities[i], message, status };
          break;
        }
      }
      const messages = [...s.messages];
      const lastIdx = messages.length - 1;
      if (lastIdx >= 0 && messages[lastIdx].id === s.streamingMessageId) {
        messages[lastIdx] = { ...messages[lastIdx], activities };
      }
      return { streamActivities: activities, messages };
    }),

  completeLastActivity: (status = 'done') =>
    set((s) => {
      const activities = [...s.streamActivities];
      for (let i = activities.length - 1; i >= 0; i--) {
        if (activities[i].status === 'active') {
          activities[i] = { ...activities[i], status };
          break;
        }
      }
      const messages = [...s.messages];
      const lastIdx = messages.length - 1;
      if (lastIdx >= 0 && messages[lastIdx].id === s.streamingMessageId) {
        messages[lastIdx] = { ...messages[lastIdx], activities };
      }
      return { streamActivities: activities, messages };
    }),

  finalizeMessage: (finalText?: string) =>
    set((s) => {
      const messages = [...s.messages];
      const lastIdx = messages.length - 1;
      // Use finalText as canonical (cleaned by backend), fall back to streamText
      const content = finalText || s.streamText;

      if (lastIdx >= 0 && messages[lastIdx].id === s.streamingMessageId) {
        // Finalize the streaming message in-place
        const finalActivities = (s.streamActivities || []).map((a) =>
          a.status === 'active' ? { ...a, status: 'done' as const } : a
        );
        messages[lastIdx] = {
          ...messages[lastIdx],
          content,
          isStreaming: false,
          activities: finalActivities.length > 0 ? finalActivities : undefined,
          toolCalls: s.streamToolCalls.length > 0 ? s.streamToolCalls : undefined,
        };
      }

      // If content is empty, no tool calls, AND no activities — remove the placeholder
      if (!content && s.streamToolCalls.length === 0 && (s.streamActivities || []).length === 0
          && lastIdx >= 0 && messages[lastIdx].id === s.streamingMessageId) {
        messages.pop();
      }

      return {
        messages,
        isStreaming: false,
        streamText: '',
        streamToolCalls: [],
        streamActivities: [],
        streamingMessageId: null,
        toolStatus: null,
      };
    }),

  setError: (error) => set({ error, isStreaming: false, toolStatus: null }),

  clearMessages: () => set({ messages: [], error: null }),
}));
